import { DialogContent } from '@material-ui/core';

import { CsvTemplateHint } from 'src/components/CsvTemplateHint';
import { CustomDialog } from 'src/components/CustomDialog';
import { CustomTypography, TYPOGRAPHY_VARIANT } from 'src/components/CustomTypography';
import { CHANNEL, getChannelPerformanceLabel, PLATFORM_CHANNEL } from 'src/data/channel';
import { EMAIL_CSV_RESOURCES, getResourceDocumentLabel } from 'src/data/performance';

import { ChannelButton } from './components/ChannelButton';
import { useCsvTemplateUrl, useVisibleIntegrationChannels } from './services';
import { useHandlers } from './useHandlers';
import { useStyles } from './useStyles';

const EMAIL_INTEGRATIONS = [
  CHANNEL.hubspot,
  CHANNEL.salesforce,
  CHANNEL.marketo,
  CHANNEL.braze,
  CHANNEL.iterable,
  CHANNEL.salesforceExactTarget,
  CHANNEL.eloqua,
];

export const ConnectToChannelPopup = ({
  open,
  brandVoiceId,
  onSubmit,
  onClose,
  onConnectSuccess,
  displayIntegrations,
  displayResourcesTemplates,
  displayEmailIntegrations,
  displayBlogURLs,
  displayTalkingPointsFromResource,
  hideWtmIntegration,
  title,
  subtitle,
  isTrainingModel,
}) => {
  const styles = useStyles();

  const csvTemplateUrl = useCsvTemplateUrl();
  const integrationChannels = useVisibleIntegrationChannels({
    displayBlogURLs,
    hideWtmIntegration,
    isTrainingModel,
  });

  const { handleChannelClick, handleBlogPostsArticlesClick, handleResourceClick } = useHandlers({
    brandVoiceId,
    isTrainingModel,
    onSubmit,
    onClose,
    onConnectSuccess,
  });

  return (
    <CustomDialog
      className={styles.root}
      open={open}
      onClose={onClose}
      title={title || 'Select Source'}
      data-testid="ConnectToChannelPopup"
      displayCloseIcon
      fullWidth
      hideActions
    >
      <DialogContent>
        <CustomTypography
          className={styles.description}
          variant={TYPOGRAPHY_VARIANT.regular14}
        >
          {subtitle || 'Select an integration or resource'}
        </CustomTypography>

        <div className={styles.buttonsWrapper}>
          {displayIntegrations &&
            integrationChannels.map((channel) => (
              <ChannelButton
                className={styles.channelButton}
                key={channel}
                channel={channel}
                onClick={handleChannelClick}
              />
            ))}

          {displayTalkingPointsFromResource && (
            <>
              <ChannelButton
                className={styles.channelButton}
                channel={CHANNEL.other}
                onClick={handleChannelClick}
              />
              <ChannelButton
                className={styles.channelButton}
                channel={CHANNEL.generalDocument}
                label="File (PDF, TXT...)"
                onClick={handleChannelClick}
              />
            </>
          )}

          {displayBlogURLs && (
            <ChannelButton
              className={styles.channelButton}
              channel={PLATFORM_CHANNEL.blogUrls}
              onClick={handleBlogPostsArticlesClick}
            />
          )}

          {displayEmailIntegrations && (
            <>
              {EMAIL_INTEGRATIONS.map((channel) => (
                <ChannelButton
                  className={styles.channelButton}
                  key={channel}
                  channel={channel}
                  label={`${getChannelPerformanceLabel(channel)} emails`}
                  onClick={handleChannelClick}
                />
              ))}

              {!displayResourcesTemplates && (
                <ChannelButton
                  className={styles.channelButton}
                  channel={CHANNEL.emailChannel}
                  onClick={handleChannelClick}
                />
              )}
            </>
          )}

          {displayResourcesTemplates &&
            EMAIL_CSV_RESOURCES.map((csvResource) => (
              <ChannelButton
                className={styles.channelButton}
                key={csvResource}
                channel={csvResource}
                label={`${getResourceDocumentLabel(csvResource)} (CSV)`}
                onClick={handleResourceClick}
              />
            ))}
        </div>

        {displayResourcesTemplates && (
          <CsvTemplateHint
            className={styles.csvTemplateHint}
            url={csvTemplateUrl}
            displayIcon
          />
        )}
      </DialogContent>
    </CustomDialog>
  );
};
